import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Building2, Heart, FileText, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useListingStore } from '../stores/listingStore';
import { useLeaseStore } from '../stores/leaseStore';
import EmptyState from '../components/EmptyState';
import ListingCard from '../components/ListingCard';
import ActiveLeases from '../components/ActiveLeases';
import { formatPrice, formatDate } from '../utils/formatters';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'listings';
  const { user } = useAuth();
  const { listings, favorites, toggleFavorite } = useListingStore();
  const { getRequestsByUser } = useLeaseStore();
  const [activeTab, setActiveTab] = useState(initialTab);

  if (!user) {
    navigate('/login');
    return null;
  }

  const userListings = listings.filter(listing => listing.ownerId === user.id);
  const favoriteListings = listings.filter(listing => favorites.includes(listing.id));
  const applications = getRequestsByUser(user.id, 'applicant');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    navigate(`?${newParams.toString()}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Manage your listings and saved properties</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('listings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'listings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="h-5 w-5 inline-block mr-2" />
              My Listings
            </button>
            <button
              onClick={() => handleTabChange('favorites')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'favorites'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Heart className="h-5 w-5 inline-block mr-2" />
              Saved Properties
            </button>
            <button
              onClick={() => handleTabChange('applications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="h-5 w-5 inline-block mr-2" />
              My Applications
            </button>
            <button
              onClick={() => handleTabChange('leases')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leases'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="h-5 w-5 inline-block mr-2" />
              Active Leases
            </button>
          </nav>
        </div>

        {/* Content Sections */}
        {activeTab === 'listings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">My Listings</h2>
              <Link
                to="/add-listing"
                className="flex items-center bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Listing
              </Link>
            </div>

            {userListings.length === 0 ? (
              <EmptyState type="dashboard" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userListings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    onFavorite={() => toggleFavorite(listing.id)}
                    isFavorite={favorites.includes(listing.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Saved Properties</h2>
            {favoriteListings.length === 0 ? (
              <EmptyState type="favorites" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteListings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    onFavorite={() => toggleFavorite(listing.id)}
                    isFavorite={true}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">My Applications</h2>
            {applications.length === 0 ? (
              <EmptyState type="applications" />
            ) : (
              <div className="space-y-6">
                {applications.map((application) => {
                  const listing = listings.find(l => l.id === application.listingId);
                  if (!listing) return null;

                  return (
                    <div key={application.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <div className="flex flex-col md:flex-row">
                        {/* Property Image */}
                        <div className="w-full md:w-64 h-48 md:h-auto relative">
                          <img
                            src={listing.images[0]}
                            alt={listing.title}
                            className="w-full h-full object-cover"
                          />
                          <Link
                            to={`/listings/${listing.id}`}
                            className="absolute top-2 right-2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 text-gray-600" />
                          </Link>
                        </div>

                        {/* Application Details */}
                        <div className="flex-1 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <Link
                                to={`/listings/${listing.id}`}
                                className="text-lg font-semibold text-gray-900 hover:text-indigo-600"
                              >
                                {listing.title}
                              </Link>
                              <p className="text-gray-600">{listing.location}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                              {application.status}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-500">Monthly Rent</p>
                              <p className="font-medium">{formatPrice(listing.price)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Move-in Period</p>
                              <p className="font-medium">
                                {formatDate(application.moveInDate)} - {formatDate(application.moveOutDate)}
                              </p>
                            </div>
                          </div>

                          {application.status === 'pending' && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm text-gray-600">
                                Your application is being reviewed. We'll notify you when there's an update.
                              </p>
                            </div>
                          )}

                          {application.status === 'approved' && !application.depositPaid && (
                            <div className="mt-4 pt-4 border-t bg-green-50 p-4 rounded-lg">
                              <p className="text-sm text-green-800">
                                Congratulations! Your application has been approved. Please pay the deposit to proceed.
                              </p>
                              <button
                                onClick={() => {
                                  // Handle deposit payment
                                  const { updateRequestStatus } = useLeaseStore.getState();
                                  updateRequestStatus(application.id, {
                                    status: 'approved',
                                    depositPaid: true
                                  });
                                }}
                                className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                Pay Deposit
                              </button>
                            </div>
                          )}

                          {application.status === 'approved' && application.depositPaid && (
                            <div className="mt-4 pt-4 border-t bg-green-50 p-4 rounded-lg">
                              <p className="text-sm text-green-800">
                                Deposit paid successfully! Check your messages for next steps and contact information.
                              </p>
                            </div>
                          )}

                          {application.status === 'rejected' && (
                            <div className="mt-4 pt-4 border-t bg-red-50 p-4 rounded-lg">
                              <p className="text-sm text-red-800">
                                Unfortunately, your application was not approved at this time.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'leases' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Active Leases</h2>
            <ActiveLeases />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;